import { useCallback, useEffect, useState } from "react";
import { Buildings, Sparkle, Trash, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);
const BLANK = { name: "", town: "", nightly_rate: "", currency: "usd", rooms: "20",
  market: "US", route: "", near_site: "", commission_pct: "12", commission_flat: "0",
  pay_mode: "app", phone: "" };

export default function Motels() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState("board");
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [hunt, setHunt] = useState({ near: "", route: "", market: "US", currency: "usd" });
  const [stay, setStay] = useState({ check_in: today(), nights: "1", rooms: "1" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/motels").then(({ data: d }) => setData(d)).catch(oops);
  }, []);
  const loadBookings = useCallback(() => {
    api.get("/motels/bookings").then(({ data: d }) => setBookings(d)).catch(oops);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (view === "bookings") loadBookings(); }, [view, loadBookings]);
  useEffect(() => {
    if (view === "money" && !ledger) api.get("/motels/commissions").then(({ data: d }) => setLedger(d)).catch(oops);
  }, [view, ledger]);
  useEffect(() => {
    if (view === "hunt" && !suggestions) api.get("/motels/suggestions").then(({ data: d }) => setSuggestions(d)).catch(oops);
  }, [view, suggestions]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/motels", {
        ...form, nightly_rate: Number(form.nightly_rate), rooms: Number(form.rooms),
        commission_pct: Number(form.commission_pct), commission_flat: Number(form.commission_flat),
        route: form.route || null, near_site: form.near_site || null, phone: form.phone || null,
      });
      setForm(BLANK); toast.success("Motel added with AI copy and a picture"); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const findThem = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/motels/suggest", {
        near: hunt.near || null, route: hunt.route || null, market: hunt.market, currency: hunt.currency,
      });
      setSuggestions(d.suggestions); toast.success(`${d.suggestions.length} suggestions from ${d.engine_label}`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/motels/suggestions/${id}/approve`);
      setSuggestions((s) => s.filter((x) => x.id !== id));
      toast.success("On the board"); load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const reject = async (id) => {
    try {
      await api.post(`/motels/suggestions/${id}/reject`);
      setSuggestions((s) => s.filter((x) => x.id !== id));
    } catch (e) { oops(e); }
  };

  const remove = async (id) => {
    try { await api.delete(`/motels/${id}`); load(); } catch (e) { oops(e); }
  };

  const redraw = async (id) => {
    setBusy(true);
    try { await api.post(`/motels/${id}/picture`); toast.success("Picture redrawn"); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  const bookIt = async (m) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/motels/bookings", {
        motel_id: m.id, check_in: stay.check_in, nights: Number(stay.nights),
        rooms: Number(stay.rooms), origin_url: window.location.origin,
      });
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      toast.success(`Booked — pay ${d.symbol || ""}${d.guest_total} at the motel`);
      loadBookings();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const TABS = [["board", "Motels"], ["bookings", "Bookings"],
    ...(isAdmin ? [["hunt", "Find motels"], ["money", "Commission"]] : [])];

  return (
    <div className="space-y-5" data-testid="motels-page">
      <PageHeader overline="A bed near the yard or on the route" title="Motels" testid="motels-header"
        description="Somewhere to sleep between long shifts and along the freight routes. We earn a commission on every booking — the guest price, our cut and the motel's payout are on every line." />

      <div className="flex flex-wrap items-center gap-2">
        <a href="/motels" target="_blank" rel="noreferrer" data-testid="motels-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public motel board
        </a>
        <ShareBar url={`${BACKEND}/api/share/motels`} text="Motels near the yard and on the route"
          testid="motels-share" compact instagram />
        {isAdmin && (
          <Button variant="outline" data-testid="motels-refresh" disabled={busy}
            onClick={async () => { setBusy(true); try { await api.post("/motels/refresh"); toast.success("Board rewritten and pictures redrawn"); load(); } catch (e) { oops(e); } finally { setBusy(false); } }}>
            <Sparkle size={15} className="mr-1.5" /> Refresh the board
          </Button>
        )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="scroll-x w-full justify-start overflow-x-auto" data-testid="motels-tabs">
          {TABS.map(([key, label]) => (
            <TabsTrigger key={key} value={key} data-testid={`motels-tab-${key}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === "board" && (
        <div className="space-y-4" data-testid="motels-board">
          <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
            <label className="text-xs font-semibold text-slate-600">Check in
              <Input data-testid="motels-check-in" type="date" value={stay.check_in}
                onChange={(e) => setStay({ ...stay, check_in: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Nights
              <Input data-testid="motels-nights" type="number" className="w-20" value={stay.nights}
                onChange={(e) => setStay({ ...stay, nights: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">Rooms
              <Input data-testid="motels-rooms" type="number" className="w-20" value={stay.rooms}
                onChange={(e) => setStay({ ...stay, rooms: e.target.value })} />
            </label>
          </div>

          {data?.can_manage && (
            <div className="grid gap-2 border border-border bg-white p-4 sm:grid-cols-4" data-testid="motels-form">
              <Input data-testid="motels-name" className="sm:col-span-2" placeholder="Roadside Inn"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="motels-town" placeholder="Town" value={form.town}
                onChange={(e) => setForm({ ...form, town: e.target.value })} />
              <Input data-testid="motels-rate" type="number" placeholder="Rate a night"
                value={form.nightly_rate} onChange={(e) => setForm({ ...form, nightly_rate: e.target.value })} />
              <Input data-testid="motels-near" placeholder="Near which yard" value={form.near_site}
                onChange={(e) => setForm({ ...form, near_site: e.target.value })} />
              <Input data-testid="motels-route" placeholder="Or which route" value={form.route}
                onChange={(e) => setForm({ ...form, route: e.target.value })} />
              <Input data-testid="motels-commission" type="number" placeholder="Commission %"
                value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} />
              <Input data-testid="motels-flat" type="number" placeholder="Flat fee"
                value={form.commission_flat} onChange={(e) => setForm({ ...form, commission_flat: e.target.value })} />
              <select data-testid="motels-pay-mode" className="border border-border bg-white px-2 py-2 text-base md:text-sm"
                value={form.pay_mode} onChange={(e) => setForm({ ...form, pay_mode: e.target.value })}>
                <option value="app">Guest pays by card here</option>
                <option value="direct">Guest pays the motel direct</option>
              </select>
              <Button data-testid="motels-add" onClick={add}
                disabled={busy || form.name.trim().length < 2 || !form.town || !form.nightly_rate}>
                {busy ? "Writing…" : "Add motel"}
              </Button>
            </div>
          )}

          {data?.motels?.length === 0 ? (
            <EmptyState testid="motels-empty" title="No motels yet"
              description="Add one, or let AI find motels near a yard or along a route." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="motels-list">
              {data?.motels?.map((m, i) => (
                <div key={m.id} data-testid={`motel-${i}`} className="border border-border bg-white">
                  {m.image_url && <img src={`${BACKEND}${m.image_url}`} alt={m.name} className="h-40 w-full object-cover" />}
                  <div className="p-3">
                    <p className="font-semibold text-[#0F172A]">{m.name}</p>
                    <p className="text-xs text-slate-500">{m.town}{m.near_site ? ` · near ${m.near_site}` : ""}{m.route ? ` · ${m.route}` : ""}</p>
                    <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{m.symbol}{m.nightly_rate}<span className="text-xs font-semibold text-slate-500"> a night</span></p>
                    {m.description && <p className="mt-1 line-clamp-3 text-sm text-slate-700">{m.description}</p>}
                    {isAdmin && (
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500" data-testid={`motel-commission-${i}`}>
                        {m.commission_pct}%{m.commission_flat ? ` + ${m.symbol}${m.commission_flat}` : ""} · {m.pay_mode === "app" ? "card here" : "pays motel"}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button size="sm" data-testid={`motel-book-${i}`} onClick={() => bookIt(m)} disabled={busy}>
                        Book {stay.nights} night{Number(stay.nights) > 1 ? "s" : ""}
                      </Button>
                      <ShareBar url={`${BACKEND}/api/share/motel/${m.id}`} text={m.name}
                        testid={`motel-share-${i}`} compact instagram />
                      {isAdmin && (
                        <>
                          <button data-testid={`motel-redraw-${i}`} onClick={() => redraw(m.id)}
                            className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#002FA7]">
                            <Sparkle size={13} />
                          </button>
                          <button data-testid={`motel-delete-${i}`} onClick={() => remove(m.id)}
                            className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]">
                            <Trash size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "bookings" && (
        bookings?.length === 0 ? (
          <EmptyState testid="motel-bookings-empty" title="No bookings yet"
            description="Book a bed from the Motels tab and it appears here." />
        ) : (
          <ul className="space-y-2" data-testid="motel-bookings">
            {bookings?.map((b, i) => (
              <li key={b.id} data-testid={`motel-booking-${i}`}
                className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                <span className="font-semibold text-[#0F172A]">{b.motel_name}</span>
                <span className="text-slate-600">{b.check_in} → {b.check_out} · {b.nights} night(s) · {b.currency} {b.guest_total}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{b.payment_status}</span>
                {b.status !== "cancelled" && (
                  <button data-testid={`motel-booking-cancel-${i}`} className="tap-safe text-xs font-semibold text-slate-500 hover:text-[#B3241B]"
                    onClick={async () => { try { await api.post(`/motels/bookings/${b.id}/cancel`); loadBookings(); } catch (e) { oops(e); } }}>
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {view === "hunt" && (
        <div className="space-y-3" data-testid="motels-hunt">
          <div className="flex flex-wrap items-end gap-2 border border-border bg-white p-4">
            <Input data-testid="motels-hunt-near" className="w-full sm:w-52" placeholder="Near which yard or town"
              value={hunt.near} onChange={(e) => setHunt({ ...hunt, near: e.target.value })} />
            <Input data-testid="motels-hunt-route" className="w-full sm:w-52" placeholder="Or a route: Detroit to Chicago"
              value={hunt.route} onChange={(e) => setHunt({ ...hunt, route: e.target.value })} />
            <Button data-testid="motels-hunt-go" onClick={findThem} disabled={busy || (!hunt.near && !hunt.route)}>
              <Sparkle size={15} className="mr-1.5" /> {busy ? "Looking…" : "Find motels"}
            </Button>
          </div>
          {suggestions?.length === 0 ? (
            <EmptyState testid="motels-suggestions-empty" title="Nothing suggested yet"
              description="Give a yard or a route and AI will put four options up for approval." />
          ) : (
            <ul className="space-y-2" data-testid="motels-suggestions">
              {suggestions?.map((s, i) => (
                <li key={s.id} data-testid={`motel-suggestion-${i}`} className="border border-border bg-white p-3">
                  <p className="font-semibold text-[#0F172A]">{s.name} · {s.town}</p>
                  <p className="text-sm text-slate-600">{s.why}</p>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{s.currency?.toUpperCase()} {s.nightly_rate} a night</p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" data-testid={`motel-approve-${i}`} onClick={() => approve(s.id)} disabled={busy}>
                      <Check size={14} className="mr-1" /> Put on the board
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`motel-reject-${i}`} onClick={() => reject(s.id)}>
                      <X size={14} className="mr-1" /> No
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {view === "money" && ledger && (
        <div className="space-y-4" data-testid="motels-commission">
          <div className="grid gap-3 sm:grid-cols-4">
            {[["Bookings", ledger.totals.bookings], ["Commission", ledger.totals.commission],
              ["Earned", ledger.totals.earned], ["Still due", ledger.totals.due]].map(([label, value]) => (
              <div key={label} className="border border-border bg-white p-3">
                <p className="overline text-slate-500">{label}</p>
                <p className="mt-1 text-xl font-bold text-[#0F172A]">{value}</p>
              </div>
            ))}
          </div>
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">By month</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700" data-testid="motels-months">
              {ledger.months.map((m, i) => (
                <li key={i}>{m.month} · {m.bookings} bookings · guests {m.currency} {m.guest_total} · ours {m.commission} · motels {m.payout}</li>
              ))}
              {ledger.months.length === 0 && <li className="text-slate-400">Nothing booked yet</li>}
            </ul>
          </div>
          <ul className="space-y-2" data-testid="motels-ledger">
            {ledger.ledger.map((r, i) => (
              <li key={r.id} data-testid={`motel-ledger-${i}`}
                className="flex flex-wrap items-center justify-between gap-2 border border-border bg-white p-3 text-sm">
                <span className="inline-flex items-center gap-1.5 font-semibold text-[#0F172A]">
                  <Buildings size={15} /> {r.motel}
                </span>
                <span className="text-slate-600">{r.guest} · {r.check_in} · {r.nights}n · {r.currency} {r.guest_total} → ours {r.commission}</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{r.commission_status}</span>
                {r.pay_mode === "direct" && r.commission_status === "due" && (
                  <button data-testid={`motel-settle-${i}`} className="tap-safe text-xs font-semibold text-[#002FA7] hover:underline"
                    onClick={async () => { try { await api.post(`/motels/bookings/${r.id}/settle`); setLedger(null); toast.success("Commission settled"); } catch (e) { oops(e); } }}>
                    Mark settled
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
